import type { IUserSettingsCmsTerms, SubmittalDragType } from 'app/services/userSettingsApi';

/**
 * The Proposal tab's small shared vocabulary — the three library sources, the
 * `model` → library-type map, the CMS-document derivation and the drag payload.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ★★ WHY THIS FILE EXISTS INSTEAD OF AN IMPORT FROM `submittalModel.ts` ★★
 *
 * Submittal and Proposal are the same machine built twice — two views, two sets
 * of collections, two nearly-identical templates, two `/drag/` endpoints
 * (discovery §1). Everything below LOOKS like a duplicate of `submittalModel.ts`
 * and none of it is:
 *
 *   · `PROPOSAL_FIELD_MODELS` has DIFFERENT KEYS — `prpuserdefinedfield` and
 *     `masterproposalpredefinedfield` where submittal has
 *     `submittaluserdefinedfield` and `mastersubmittalpredefinedfield`. Sharing
 *     one map would make each tab's `rowClick` accept the other's rows.
 *   · `PROPOSAL_DRAG_MODELS` is its inverse and diverges the same way.
 *   · the CMS derivation is byte-identical TODAY (`proposal.js:118-127` vs
 *     `submittal.js:137-146`) and is transcribed anyway, because two copies of a
 *     legacy loop that two independent views own is exactly the shape this port
 *     is required to preserve.
 *
 * What IS imported, from `submittalModel.ts`, is `jqHeight` and
 * `deleteButtonWantsTop`. Those are not view logic: they are shims for two
 * jQuery primitives (`$(el).height()`'s content-box semantics, and the
 * `mouseover` handler that is character-for-character identical at
 * `proposal.js:205-216` and `submittal.js:245-255` because it only reads the
 * element's own `data-parent`). Duplicating a measurement helper buys nothing
 * and would let the two copies drift.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/** `data-type` on the three chips, the three `.proposal-row` panes and the three
 * `.fields-count` labels. `self.activeDefinedsField` (`proposal.js:90`) starts
 * at `'user'`. */
export type ProposalLibraryType = 'user' | 'cms' | 'application';

/**
 * `proposal.js:91-95` `self.fields` — the format row's `model` mapped onto the
 * library that owns it. A row whose `model` is absent from this map (a
 * freshly-created placeholder, `model: null`) makes `rowClick` highlight the row
 * and stop, without touching the library.
 */
export const PROPOSAL_FIELD_MODELS: Record<string, ProposalLibraryType> = {
  masterproposalpredefinedfield: 'application',
  prpuserdefinedfield: 'user',
  cmsahuterms: 'cms',
};

/**
 * `createNewField`'s `models` map (`proposal.js:577-581`) — the drag `type`
 * mapped onto the content type's model name, i.e. the INVERSE of
 * `PROPOSAL_FIELD_MODELS`. Used only to fill `data-model` on the hand-injected
 * row so `rowClick`'s cross-highlight works on it before the server has said
 * what the row is.
 */
export const PROPOSAL_DRAG_MODELS: Record<SubmittalDragType, string> = {
  user_defined: 'prpuserdefinedfield',
  cms_terms: 'cmsahuterms',
  app_defined: 'masterproposalpredefinedfield',
};

/**
 * Everything `createNewField` (`proposal.js:560-605`) reads off a dropped
 * library item, and nothing else — four `e.attr(...)` calls on the dropped
 * helper. Which of the four are present depends on which `<a>` template was
 * dragged, and the ABSENCES are what make a CMS drop send no `object_id` and a
 * user drop send no `cms_field_name`:
 *
 *   library   data-type       data-id        data-name       data-text
 *   ────────  ──────────────  ─────────────  ──────────────  ─────────────
 *   User      —  (→ fallback) `{{id}}`       —               `{{name}}`
 *   CMS       `cms_terms`     —              `{{value}}`     `{{label}}`
 *   App       `app_defined`   `{{id}}`       —               `{{name}}`
 *
 * Read with `getAttribute` semantics (a missing attribute is `undefined`, never
 * `''` — `JSON.stringify` only drops the former).
 */
export interface ProposalDragPayload {
  /** `e.attr("data-type") || "user_defined"` (`:562`) — the `||`, not `??`, so
   * an empty attribute falls back too. */
  type: SubmittalDragType;
  /** `e.attr("data-name")` (`:563`) → the PATCH's `cms_field_name`. */
  name?: string;
  /** `e.attr("data-id")` (`:564`) → the PATCH's `object_id`, as a STRING. */
  id?: string;
  /** `e.attr("data-text")` (`:565`) → the label the hand-injected rail row shows
   * until the full re-render replaces it with the server's `name`. */
  text: string;
}

/** One row of the CMS library / one read-only detail pane. */
export interface ProposalCmsDocument {
  /** `<key>_name` — the display label, e.g. "Shipping Terms & Conditions". */
  label: string;
  /** The terms key itself, e.g. `shipping_terms`. Used as `data-cms`,
   * `data-name` and the `.{{value}}_textarea` class. */
  value: string;
  /** `cms_terms_<value>` — the anchor/pane id stem. On this tab it is the pane's
   * WHOLE id (`proposal.tmpl:390` `id="{{key}}"`), where submittal prefixes it
   * with `submittal_`. */
  key: string;
}

/**
 * `proposal.js:118-127`, transcribed literally.
 *
 * The CMS library is not a list endpoint: it is DERIVED from the terms object by
 * walking `Object.keys(terms)` and keeping every key `k` whose `k + '_name'`
 * sibling is truthy. Six keys qualify on mfg 1, in serializer-declaration order.
 *
 * ★ An mfg with fewer configured `*_name` labels legitimately shows fewer rows
 * (discovery §15.2). Do not hard-code six.
 */
export function deriveProposalCmsDocuments(
  terms: IUserSettingsCmsTerms | undefined
): ProposalCmsDocument[] {
  if (!terms) return [];
  const rec = terms as unknown as Record<string, unknown>;
  const documents: ProposalCmsDocument[] = [];
  Object.keys(rec).forEach((key) => {
    const label = rec[`${key}_name`];
    if (label) {
      documents.push({ label: String(label), value: key, key: `cms_terms_${key}` });
    }
  });
  return documents;
}

/**
 * The raw HTML body for one CMS document — `render()` `.append()`s it into
 * `#proposal_format .{{value}}_textarea` (`proposal.js:169-173`).
 *
 * ★ THAT SELECTOR MATCHES TWICE FOR `sales_terms`, and the port keeps both
 * targets: `proposal.tmpl:372-388` hard-codes an ORPHAN "Sales Terms &
 * Conditions" pane whose body div also carries `.sales_terms_textarea`, and the
 * `{{#each cms_documents}}` loop below it emits a second one. The orphan is
 * never activated (every anchor points at the loop's `#cms_terms_sales_terms`,
 * while the orphan is `#cms_terms_sales`) — discovery §10 lists it as dead code
 * INSIDE a live file. It is rendered anyway, inert and invisible, because it is
 * literal template output and a DOM-comparing verifier will see it in legacy.
 */
export function proposalCmsBody(
  terms: IUserSettingsCmsTerms | undefined,
  value: string
): string {
  if (!terms) return '';
  const body = (terms as unknown as Record<string, unknown>)[value];
  return typeof body === 'string' ? body : '';
}
